import React, {
  useContext,
  useState,
  useMemo,
  useCallback,
  useRef
} from 'react';
import { FormSpy } from 'react-final-form';
import { ManageContactContext } from './ManageContactContext';
import {
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Typography
} from '@material-ui/core/';
import {
  Loading,
  ReferenceArrayInput,
  useQuery,
  fetchStart,
  useNotify,
  fetchEnd,
  useRefresh,
  RecordContextProvider
} from 'react-admin';
import GridInput from '../../../core/components/inputs/GridInput';
import EntitlementApi from '../../../core/apis/EntitlementApi';
import withStyles from '@material-ui/core/styles/withStyles';
import GridPopover from '../../../core/components/grid/GridPopover';
import FundInvestorApi from '../../../core/apis/FundInvestorApi';
import { uniq, intersectionBy } from 'lodash';
import { useDispatch } from 'react-redux';
import useNotifyServerError from '../../../core/hooks/useNotifyServerError';
import CustomSimpleForm from '../../../core/components/form/CustomSimpleForm';

const styles = {
  button: {
    padding: '0'
  },
  loading: {
    width: '100%',
    height: '100%',
    display: 'content',
    marginTop: '0px'
  },
  paper: {
    '& > div': {
      maxHeight: 'none'
    }
  },
  tableWrapper: {
    maxHeight: 'calc(100vh - 250px)',
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '8px'
    },
    /* Track */
    '&::-webkit-scrollbar-track': {
      background: '#ebebeb',
      borderRadius: '10px',
      margin: '2px'
    },
    /* Handle */
    '&::-webkit-scrollbar-thumb': {
      background: '#9E9E9E',
      borderRadius: '10px'
    },
    /* Handle on hover */
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#7b7a7a'
    }
  }
};
const GridInputTable = ({ groups, index }) => {
  return (
    <ReferenceArrayInput
      label=" "
      optionTextLabel="Permission Group Name"
      optionValueLabel="group ID"
      resource="groups"
      reference="groups"
      source={`users[${index}].selectedGroups`}
      perPage={-1}
      sort={{ field: 'name', order: 'ASC' }}
      filter={{
        id: `[${groups}]`
      }}
    >
      <GridInput />
    </ReferenceArrayInput>
  );
};
const ContactsList = ({ classes, userTree, selectedUsers }) => {
  const [selectedRows, setSelectedRows] = useState([]);
  selectedUsers.current = selectedRows;

  /**
   * Calculating number of rows depending of the filter
   */
  const numSelectedRows = useMemo(() => {
    let newArray = [];
    userTree.forEach(item => {
      if (selectedRows.includes(item)) {
        newArray.push(item);
      }
    });
    return newArray.length;
  }, [userTree, selectedRows]);

  const rowCount = userTree.length;

  const handleClickAll = useCallback(() => {
    if (numSelectedRows === rowCount) {
      setSelectedRows([]);
    } else if (numSelectedRows !== rowCount) {
      setSelectedRows(userTree);
    }
  }, [numSelectedRows, userTree, rowCount]);

  /**
   * When we click on the checkbox in a row
   * @param {*} checked
   * @param {*} item
   */
  const handleRowSelect = useCallback((checked, item) => {
    if (checked) {
      setSelectedRows(selectedRows => selectedRows.concat(item));
    } else {
      setSelectedRows(selectedRows =>
        selectedRows.filter(itemRow => item !== itemRow)
      );
    }
  }, []);

  if (userTree.length === 0)
    return <>There are no related Contacts to be displayed</>;

  return (
    <>
      <div className={classes.tableWrapper}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={
                    numSelectedRows > 0 && numSelectedRows < rowCount
                  }
                  checked={numSelectedRows === rowCount && rowCount > 0}
                  onClick={handleClickAll}
                />
              </TableCell>
              <TableCell>Contact Name</TableCell>
              <TableCell>Contact Email</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Permission Groups</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {userTree &&
              userTree.map((user, index) => {
                return (
                  <TableRow key={index}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        onClick={event =>
                          handleRowSelect(event.target.checked, user)
                        }
                        checked={selectedRows.includes(user)}
                      />
                    </TableCell>
                    <TableCell>{`${user.firstName} ${user.lastName}`}</TableCell>
                    <TableCell>{`${user.email}`}</TableCell>
                    <TableCell>{user.company}</TableCell>
                    <TableCell>
                      {user.groups.length > 0 ? (
                        <GridPopover
                          text="Manage"
                          classes={{
                            paper: classes.paper,
                            button: classes.button
                          }}
                          popoverContent={
                            <GridInputTable
                              groups={user.groups}
                              index={index}
                            />
                          }
                        />
                      ) : (
                        'No permission Groups'
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </div>
      <Grid item xs={12}>
        <Grid container justifyContent="flex-end">
          <Typography variant="caption">
            {selectedRows.length} item{selectedRows.length > 1 && 's'} selected
          </Typography>
        </Grid>
      </Grid>
    </>
  );
};

const ContactsStep = ({ operation, ...props }) => {
  const { contactsContext, setContactsContext } = useContext(
    ManageContactContext
  );

  const selectedUsers = useRef([]);
  const dispatch = useDispatch();
  const notify = useNotify();
  const notifyServerError = useNotifyServerError();
  const refresh = useRefresh();

  const onSubmit = async values => {
    dispatch(fetchStart()); // start the global loading indicator
    const fundinvestors = [];
    if (props.fundId) {
      contactsContext.investors.forEach(investorId => {
        fundinvestors.push({
          fundId: props.fundId,
          investorId
        });
      });
    } else {
      contactsContext.funds.forEach(fundId => {
        fundinvestors.push({
          fundId,
          investorId: props.investorId
        });
      });
    }
    try {
      await FundInvestorApi.create(fundinvestors);
    } catch (e) {
      notifyServerError(e);
    }

    let userNotificationsArray = [];
    if (contactsContext.funds.length > 0) {
      contactsContext.funds.forEach(fundId => {
        intersectionBy(values.users, selectedUsers.current, 'userId').forEach(
          user => {
            user.selectedGroups.forEach(groupId => {
              let finalUser = {
                userId: user.userId,
                fundId: fundId,
                investorId: props.investorId,
                groupId
              };
              userNotificationsArray.push(finalUser);
            });
          }
        );
      });
    } else if (contactsContext.investors.length > 0) {
      contactsContext.investors.forEach(investorId => {
        intersectionBy(values.users, selectedUsers.current, 'userId').forEach(
          user => {
            user.selectedGroups.forEach(groupId => {
              let finalUser = {
                userId: user.userId,
                fundId: props.id,
                investorId: investorId,
                groupId
              };
              userNotificationsArray.push(finalUser);
            });
          }
        );
      });
    }

    if (userNotificationsArray.length > 0) {
      try {
        await EntitlementApi.createAuthorizations(userNotificationsArray);
        notify('ra.notification.custom_updated_message');
        setContactsContext({
          currentForm: undefined
        });
        await props.onSubmitEnd();
        refresh();
      } catch (e) {
        notifyServerError(e);
      } finally {
        dispatch(fetchEnd()); // finish the global loading indicator
      }
    } else {
      notify('ra.notification.created');
      setContactsContext({
        currentForm: undefined
      });
      await props.onSubmitEnd();
      refresh();
    }
  };

  const { data: userauthorizations = [] } = useQuery({
    type: 'getManyReference',
    resource: 'userauthorizations',
    payload: {
      filter: {
        investorId: props.fundId
          ? `[${contactsContext.investors}]`
          : props.investorId
      }
    }
  });

  // Render after querying the recipients
  const { data, loading } = useQuery({
    type: 'getManyReference',
    resource: 'investorcontacts',
    payload: {
      target: 'investorId',
      id: props.fundId ? `[${contactsContext.investors}]` : props.investorId,
      sort: {
        field: 'firstName',
        order: 'ASC'
      }
    }
  });

  if (loading) return <Loading className={props.classes.loading} />;
  if (data) {
    let userTree;

    userTree = data.reduce((contacts, user) => {
      let groups = [];
      userauthorizations &&
        userauthorizations.forEach(userauthorization => {
          if (userauthorization.userId === user.userId) {
            groups.push(userauthorization.groupId);
          }
        });
      user.groups = uniq(groups);
      user.selectedGroups = uniq(groups);
      return contacts.concat(user);
    }, []);

    return (
      <RecordContextProvider value={{ users: userTree }}>
        <CustomSimpleForm
          save={onSubmit}
          toolbar={null}
          // We override the redux-form name to avoid collision with the react-admin main form
          resource="investorcontacts"
          variant="standard"
          margin="normal"
        >
          <FormSpy>
            {formprops => {
              if (contactsContext.currentForm === undefined) {
                setContactsContext({
                  ...contactsContext,
                  currentForm: formprops.form
                });
              }
              return (
                <Grid container spacing={1}>
                  <Grid item sm={12}>
                    <ContactsList
                      investorId={props.investorId}
                      userTree={userTree}
                      selectedUsers={selectedUsers}
                      {...props}
                    />
                  </Grid>
                </Grid>
              );
            }}
          </FormSpy>
        </CustomSimpleForm>
      </RecordContextProvider>
    );
  }
};

export default withStyles(styles)(ContactsStep);
